import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = ({ currentLanguage, userType, onUserTypeChange }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const heroSlides = [
    {
      id: 1,
      title: {
        en: "Authentic Voices, Measurable Results",
        ar: "أصوات أصيلة، نتائج قابلة للقياس"
      },
      subtitle: {
        en: "Where brands meet their perfect storytellers",
        ar: "حيث تلتقي العلامات التجارية برواة قصصها المثاليين"
      },
      description: {
        en: "Connect with Iraq\'s most authentic content creators and transform your brand message into culturally resonant stories that audiences actually want to engage with.",
        ar: "تواصل مع أكثر منشئي المحتوى أصالة في العراق وحوّل رسالة علامتك التجارية إلى قصص ثقافية مؤثرة يرغب الجمهور فعلاً في التفاعل معها."
      },
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop&crop=faces",
      stats: {
        creators: "2,500+",
        campaigns: "850+",
        brands: "120+"
      }
    },
    {
      id: 2,
      title: {
        en: "Creating Content That Connects Cultures",
        ar: "إنشاء محتوى يربط الثقافات"
      },
      subtitle: {
        en: "Bridge the gap between brands and authentic Middle Eastern voices",
        ar: "اربط الفجوة بين العلامات التجارية والأصوات الشرق أوسطية الأصيلة"
      },
      description: {
        en: "Our platform empowers creators with professional tools while helping brands discover talent that truly understands their target audience's cultural nuances.",
        ar: "منصتنا تمكن المبدعين بأدوات احترافية بينما تساعد العلامات التجارية على اكتشاف المواهب التي تفهم حقاً الفروق الثقافية لجمهورها المستهدف."
      },
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?w=800&h=600&fit=crop",
      stats: {
        creators: "2,500+",
        campaigns: "850+",
        brands: "120+"
      }
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [heroSlides?.length]);

  const currentSlideData = heroSlides?.[currentSlide];

  const handleGetStarted = () => {
    if (userType === 'brand') {
      window.location.href = '/campaign-marketplace';
    } else {
      window.location.href = '/creator-profiles';
    }
  };

  const handleExploreCreators = () => {
    window.location.href = '/creator-profiles';
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-brand-blue">
      {/* Geometric Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-32 h-32 border-2 border-brand-gold rotate-45 animate-geometric"></div>
        <div className="absolute top-40 right-32 w-24 h-24 border-2 border-white rotate-12 animate-geometric" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-40 w-20 h-20 border-2 border-brand-gold rotate-45 animate-geometric" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-20 w-28 h-28 border-2 border-white rotate-12 animate-geometric" style={{ animationDelay: '3s' }}></div>
      </div>
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <Image
          src={currentSlideData?.image}
          alt="Hero Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/75"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Section */}
          <div className={`text-white transition-all duration-1000 ease-cultural ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* User Type Toggle */}
            <div className="inline-flex bg-white/10 backdrop-blur-sm rounded-full p-1 mb-8">
              <button
                onClick={() => onUserTypeChange('brand')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  userType === 'brand' ?'bg-white text-primary shadow-subtle' :'text-white/80 hover:text-white'
                }`}
              >
                {currentLanguage === 'ar' ? 'للعلامات التجارية' : 'For Brands'}
              </button>
              <button
                onClick={() => onUserTypeChange('creator')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  userType === 'creator' ?'bg-white text-primary shadow-subtle' :'text-white/80 hover:text-white'
                }`}
              >
                {currentLanguage === 'ar' ? 'للمبدعين' : 'For Creators'}
              </button>
            </div>

            <h1 className="font-headline text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              {currentSlideData?.title?.[currentLanguage]}
            </h1>

            <h2 className="font-accent text-xl lg:text-2xl mb-6 text-brand-gold">
              {currentSlideData?.subtitle?.[currentLanguage]}
            </h2>

            <p className="font-body text-lg lg:text-xl mb-8 text-white/90 leading-relaxed max-w-2xl">
              {currentSlideData?.description?.[currentLanguage]}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleGetStarted}
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-brand-gold hover:bg-brand-gold/90 text-primary font-cta"
              >
                {currentLanguage === 'ar' ? 'ابدأ الآن' : 'Get Started'}
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={handleExploreCreators}
                iconName="Users"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10 font-cta"
              >
                {currentLanguage === 'ar' ? 'استكشف المبدعين' : 'Explore Creators'}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8">
              <div className="text-center">
                <div className="font-headline text-2xl lg:text-3xl font-bold text-brand-gold mb-1 animate-odometer">
                  {currentSlideData?.stats?.creators}
                </div>
                <div className="text-sm text-white/80">
                  {currentLanguage === 'ar' ? 'مبدع' : 'Creators'}
                </div>
              </div>
              <div className="text-center">
                <div className="font-headline text-2xl lg:text-3xl font-bold text-brand-gold mb-1 animate-odometer">
                  {currentSlideData?.stats?.campaigns}
                </div>
                <div className="text-sm text-white/80">
                  {currentLanguage === 'ar' ? 'حملة' : 'Campaigns'}
                </div>
              </div>
              <div className="text-center">
                <div className="font-headline text-2xl lg:text-3xl font-bold text-brand-gold mb-1 animate-odometer">
                  {currentSlideData?.stats?.brands}
                </div>
                <div className="text-sm text-white/80">
                  {currentLanguage === 'ar' ? 'علامة تجارية' : 'Brands'}
                </div>
              </div>
            </div>
          </div>

          {/* Visual Section */}
          <div className={`relative transition-all duration-1000 ease-cultural ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative">
              {/* Main Hero Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-prominent hover-lift">
                <Image
                  src={currentSlideData?.image}
                  alt="CreatorBridge Platform"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Floating Stats Card */}
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-conversion-green rounded-full flex items-center justify-center">
                        <Icon name="TrendingUp" size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {currentLanguage === 'ar' ? 'نمو المنصة' : 'Platform Growth'}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {currentLanguage === 'ar' ? '+45% هذا الشهر' : '+45% this month'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-conversion-green">98.5%</div>
                      <div className="text-xs text-text-secondary">
                        {currentLanguage === 'ar' ? 'رضا العملاء' : 'Satisfaction'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-brand-gold/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/20 rounded-full blur-lg"></div>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center mt-12 space-x-2">
          {heroSlides?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-brand-gold scale-125' :'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="flex flex-col items-center text-white/60">
            <span className="text-sm mb-2">
              {currentLanguage === 'ar' ? 'اكتشف المزيد' : 'Discover More'}
            </span>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;